import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Alert,
  styled,
  TextField,
  Autocomplete,
  Button,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { toast } from 'sonner';
import { logger } from '../utils/logger';
import { fetchErrorPatterns, fetchErrorPatternEnums, fetchErrorPatternsCount, createErrorPattern } from '../api/features/errorPattern/errorPatternApi';
import { fetchClients } from '../api/features/clientData/clientApi';
import { ErrorPatternData, CreateErrorPatternPayload } from '../api/features/errorPattern/errorPattern.types';
import { useNavigate } from 'react-router-dom';
import { SAPIconButton, PageHeader } from '../components';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { SAP_COLORS, SAP_SPACING, SAP_TYPOGRAPHY, SAP_SHADOWS } from '../theme/sapDesignSystem';

// SAP Fiori Error Pattern Components - matching JobDetail exactly

const SAPFilterCard = styled(Card)(({ theme }) => ({
  marginBottom: SAP_SPACING.lg,
  borderRadius: '8px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.card,
}));

const SAPDataCard = styled(Card)(({ theme }) => ({
  borderRadius: '8px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.card,
}));

const SAPTableHeader = styled(TableHead)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapGray1,
  '& .MuiTableCell-head': {
    fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
    color: SAP_COLORS.sapGray6,
    borderBottom: `2px solid ${SAP_COLORS.sapBlue}`,
  },
}));

const SAPStatusChip = styled('div')(({ status }: { status: string }) => {
  const getStatusColor = (status: string) => {
    if (status === 'TECHNICAL_ERROR') return { bg: SAP_COLORS.sapLightRed, text: SAP_COLORS.sapRed };
    if (status === 'BUSINESS_ERROR') return { bg: SAP_COLORS.sapLightOrange, text: SAP_COLORS.sapOrange };
    if (status === 'DEFAULT') return { bg: SAP_COLORS.sapGray2, text: SAP_COLORS.sapGray6 };
    return { bg: SAP_COLORS.sapGray2, text: SAP_COLORS.sapGray6 };
  };
  
  const colors = getStatusColor(status);
  
  return {
    backgroundColor: colors.bg,
    color: colors.text,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
    fontSize: '0.75rem',
    padding: '4px 8px',
    borderRadius: '12px',
    display: 'inline-block',
    textAlign: 'center' as const,
  };
});

const ROWS_PER_PAGE = 10;

function getErrorTypeDisplay(row: ErrorPatternData): { display: string; status: string } {
  const val = row.isTechnicalError ?? row.errorType;
  if (val == null || val === '') {
    return { display: 'Default', status: 'DEFAULT' };
  }
  if (val === true || val === 'true') return { display: 'TECHNICAL_ERROR', status: 'TECHNICAL_ERROR' };
  if (val === false || val === 'false') return { display: 'BUSINESS_ERROR', status: 'BUSINESS_ERROR' };
  const s = String(val);
  if (s === 'TECHNICAL_ERROR' || s === 'BUSINESS_ERROR') return { display: s, status: s };
  return { display: 'Default', status: 'DEFAULT' };
}

export default function ErrorPattern() {
  const { clientCode } = useClientCode();
  const navigate = useNavigate();
  const isAdmin = clientCode === 'admin';
  
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [selectedErrorType, setSelectedErrorType] = useState<any>(null);
  const [errorCode, setErrorCode] = useState<string>('');
  const [clientOptions, setClientOptions] = useState<any[]>([]);
  const [clientMap, setClientMap] = useState<Record<string, string>>({});
  const [errorTypeOptions, setErrorTypeOptions] = useState<any[]>([]);
  const [errorPatternData, setErrorPatternData] = useState<ErrorPatternData[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createClient, setCreateClient] = useState<any>(null);
  const [createErrorType, setCreateErrorType] = useState<any>(null);
  const [createPattern, setCreatePattern] = useState('');
  const [createErrorCode, setCreateErrorCode] = useState('');
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createClientOptions, setCreateClientOptions] = useState<any[]>([]);

  // Fetch client options (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    const fetchClientOptions = async () => {
      try {
        const clients = await fetchClients({ 
          clientCode, 
          firstResult: -1, 
          maxResult: 0 
        });
        
        const clientOptions = clients.map((client: any) => ({ 
          label: client.name, 
          value: client.id,
          code: client.code 
        }));
        setClientOptions(clientOptions);
        
        // Create a map of clientId to clientName for easy lookup
        const clientIdToNameMap: Record<string, string> = {};
        clients.forEach((client: any) => {
          clientIdToNameMap[client.id] = client.name;
        });
        setClientMap(clientIdToNameMap);
      } catch (err) {
        logger.error('Failed to fetch clients:', err);
        setClientOptions([]);
      }
    };

    fetchClientOptions();
  }, [clientCode, isAdmin]);

  // Fetch error type options (uses current clientCode)
  useEffect(() => {
    const fetchErrorTypeOptions = async () => {
      try {
        const enums = await fetchErrorPatternEnums({ clientCode });
        logger.log('Error pattern enums response:', enums);
        
        if (enums && Array.isArray(enums)) {
          // Handle array of arrays - each item is an array with error types
          const errorTypeOptions = enums.map((typeArray: any) => {
            // Each item is an array like ["BUSINESS_ERROR", "TECHNICAL_ERROR"]
            if (Array.isArray(typeArray) && typeArray.length > 0) {
              return typeArray.map((type: string) => ({ 
                label: type, 
                value: type 
              }));
            }
            return [];
          }).flat(); // Flatten the array of arrays
          setErrorTypeOptions(errorTypeOptions);
        } else if (enums && typeof enums === 'object') {
          // Handle case where enums is an object with label/value structure
          const errorTypeOptions = Object.keys(enums).map((key) => ({ 
            label: key, 
            value: key 
          }));
          setErrorTypeOptions(errorTypeOptions);
        } else {
          setErrorTypeOptions([]);
        }
      } catch (err) {
        logger.error('Failed to fetch error type options:', err);
        setErrorTypeOptions([]);
      }
    };

    fetchErrorTypeOptions();
  }, []);

  // Fetch error pattern data and count
  const fetchData = async (pageOverride?: number) => {
    setLoading(true);
    setError(null);
    try {
      const currentPage = pageOverride ?? page;
      
      // Use pagination: firstResult = page * rowsPerPage, maxResult = rowsPerPage
      const params: any = {
        clientCode,
        firstResult: currentPage * ROWS_PER_PAGE,
        maxResult: ROWS_PER_PAGE,
        searched: true,
      };
      
      // Only add parameters if they are selected
      if (selectedClient?.value) params.clientId = selectedClient.value;
      if (selectedErrorType?.value) params.isTechnicalError = selectedErrorType.value;
      if (errorCode) params.errorCode = errorCode;
      
      const [errorPatternResponse, errorPatternCount] = await Promise.all([
        fetchErrorPatterns(params),
        fetchErrorPatternsCount({
          clientCode,
          searched: true,
          ...(isAdmin && selectedClient?.value && { clientId: selectedClient.value }),
          ...(selectedErrorType?.value && { isTechnicalError: selectedErrorType.value }),
          ...(errorCode && { errorCode }),
        }),
      ]);
      
      logger.log('Error Pattern API Response:', { errorPatternResponse, errorPatternCount, countType: typeof errorPatternCount });
      
      setErrorPatternData(errorPatternResponse);
      setRowCount(errorPatternCount || 0);
    } catch (err: any) {
      setError('Failed to load error pattern data');
      setErrorPatternData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    setHasSearched(true);
    fetchData(0);
  };

  const handleReset = () => {
    setSelectedClient(null);
    setSelectedErrorType(null);
    setErrorCode('');
    setPage(0);
    setHasSearched(false);
    setError(null);
    setErrorPatternData([]);
    setRowCount(0);
  };

  const handleCreateOpen = () => {
    setCreateDialogOpen(true);
    setCreateClient(null);
    setCreateErrorType(null);
    setCreatePattern('');
    setCreateErrorCode('');
    setCreateError(null);
    if (isAdmin) {
      setCreateClientOptions(clientOptions);
    } else {
      fetchClients({ clientCode, firstResult: -1, maxResult: 0 })
        .then((clients) => {
          const opts = clients.map((c: any) => ({ label: c.name, value: c.id, code: c.code }));
          setCreateClientOptions(opts);
          if (opts.length > 0) setCreateClient(opts[0]);
        })
        .catch(() => setCreateClientOptions([]));
    }
  };

  const handleCreateClose = () => {
    if (!createSaving) {
      setCreateDialogOpen(false);
      setCreateClient(null);
      setCreateErrorType(null);
      setCreatePattern('');
      setCreateErrorCode('');
      setCreateError(null);
    }
  };

  const handleCreateSave = async () => {
    const clientId = createClient?.value;
    if (clientId == null || clientId === '') {
      setCreateError('Please select a client.');
      return;
    }
    if (!createErrorType?.value) {
      setCreateError('Please select an error type.');
      return;
    }
    if (!createPattern.trim()) {
      setCreateError('Please enter a pattern.');
      return;
    }
    if (!createErrorCode.trim()) {
      setCreateError('Please enter an error code.');
      return;
    }
    setCreateSaving(true);
    setCreateError(null);
    try {
      const payload: CreateErrorPatternPayload = {
        clientId: Number(clientId),
        isTechnicalError: createErrorType.value,
        pattern: createPattern.trim(),
        errorCode: createErrorCode.trim(),
      };
      await createErrorPattern({ clientCode, payload });
      toast.success('Error pattern created successfully');
      setCreateDialogOpen(false);
      setCreateClient(null);
      setCreateErrorType(null);
      setCreatePattern('');
      setCreateErrorCode('');
      setHasSearched(true);
      fetchData(0);
      setPage(0);
    } catch (err: any) {
      setCreateError(err?.message || 'Failed to create error pattern');
    } finally {
      setCreateSaving(false);
    }
  };

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
    fetchData(newPage);
  };

  const handleClientChange = (value: any) => {
    setSelectedClient(value);
  };

  const handleErrorTypeChange = (value: any) => {
    setSelectedErrorType(value);
  };

  const handleErrorCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setErrorCode(event.target.value);
  };

  return (
    <Box sx={{ p: SAP_SPACING.lg, bgcolor: SAP_COLORS.sapGray1, minHeight: '100vh' }}>
      {/* Page Header */}
      <PageHeader 
        title="Error Pattern Search"
        description="Search and manage error patterns across all clients"
      />

      {/* SAP Fiori Filter Card - exactly like JobDetail */}
      <SAPFilterCard>
        <CardContent sx={{ p: SAP_SPACING.lg }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr auto' }, 
            gap: SAP_SPACING.md,
            alignItems: 'end'
          }}>
            {/* Client Name Autocomplete - admin only */}
            {isAdmin && (
              <Box>
                <Autocomplete
                  options={clientOptions}
                  value={selectedClient}
                  onChange={(_, v) => handleClientChange(v)}
                  getOptionLabel={(option) => option?.label || ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Client Name"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: SAP_COLORS.sapWhite,
                          '& fieldset': { borderColor: SAP_COLORS.sapGray3 },
                          '&:hover fieldset': { borderColor: SAP_COLORS.sapBlue },
                          '&.Mui-focused fieldset': { borderColor: SAP_COLORS.sapBlue },
                        },
                        '& .MuiInputLabel-root': { color: SAP_COLORS.sapGray5 },
                        '& .MuiInputBase-input': { color: SAP_COLORS.sapGray7 },
                      }}
                    />
                  )}
                  disabled={loading}
                  isOptionEqualToValue={(option, value) => option?.value === value?.value}
                />
              </Box>
            )}
            
            {/* Error Type Autocomplete */}
            <Box>
              <Autocomplete
                options={errorTypeOptions}
                value={selectedErrorType}
                onChange={(_, v) => handleErrorTypeChange(v)}
                getOptionLabel={(option) => option?.label || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Error Type"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: SAP_COLORS.sapWhite,
                        '& fieldset': { borderColor: SAP_COLORS.sapGray3 },
                        '&:hover fieldset': { borderColor: SAP_COLORS.sapBlue },
                        '&.Mui-focused fieldset': { borderColor: SAP_COLORS.sapBlue },
                      },
                      '& .MuiInputLabel-root': { color: SAP_COLORS.sapGray5 },
                      '& .MuiInputBase-input': { color: SAP_COLORS.sapGray7 },
                    }}
                  />
                )}
                disabled={loading}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
              />
            </Box>
            
            {/* Error Code Input */}
            <TextField
              label="Error Code"
              value={errorCode}
              onChange={handleErrorCodeChange}
              placeholder="Enter error code"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: SAP_COLORS.sapWhite,
                  '& fieldset': { borderColor: SAP_COLORS.sapGray3 },
                  '&:hover fieldset': { borderColor: SAP_COLORS.sapBlue },
                  '&.Mui-focused fieldset': { borderColor: SAP_COLORS.sapBlue },
                },
                '& .MuiInputLabel-root': { color: SAP_COLORS.sapGray5 },
                '& .MuiInputBase-input': { color: SAP_COLORS.sapGray7 },
              }}
            />
            
            {/* Action Buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', sm: 'flex-end' }
            }}>
              <SAPIconButton
                icon="search"
                variant="primary"
                onClick={handleSearch}
                disabled={loading}
                aria-label="Search"
              />
              <SAPIconButton
                icon="reset"
                variant="secondary"
                onClick={handleReset}
                disabled={loading}
                aria-label="Reset"
              />
              <SAPIconButton
                icon="add"
                variant="success"
                onClick={handleCreateOpen}
                disabled={loading}
                aria-label="Create"
              />
            </Box>
          </Box>
        </CardContent>
      </SAPFilterCard>

      {/* SAP Fiori Data Card - exactly like JobDetail */}
      <SAPDataCard>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
              <CircularProgress sx={{ color: SAP_COLORS.sapBlue }} size={48} />
            </Box>
          ) : error ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
              <Alert severity="error" sx={{ 
                borderRadius: '8px',
                backgroundColor: SAP_COLORS.sapRed + '10',
                color: SAP_COLORS.sapRed,
                border: `1px solid ${SAP_COLORS.sapRed}30`,
              }}>
                {error}
              </Alert>
            </Box>
          ) : !hasSearched && errorPatternData.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
              <Typography variant="body1" sx={{ color: SAP_COLORS.sapGray5 }}>
                Please use the search filters above to view error pattern data.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table sx={{ minWidth: 650 }}>
                  {/* SAP Fiori Table Header */}
                  <SAPTableHeader>
                    <TableRow>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6, 
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
                        width: '80px'
                      }}>
                        S.No.
                      </TableCell>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6, 
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold 
                      }}>
                        Client Name
                      </TableCell>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6, 
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold 
                      }}>
                        Pattern
                      </TableCell>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6, 
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold 
                      }}>
                        Error Code
                      </TableCell>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6, 
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold 
                      }}>
                        Error Type
                      </TableCell>
                    </TableRow>
                  </SAPTableHeader>
                  
                  {/* Table Body */}
                  <TableBody>
                    {errorPatternData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ 
                          color: SAP_COLORS.sapGray5,
                          py: SAP_SPACING.xl,
                          fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                        }}>
                          No error pattern data found. Try searching with different criteria.
                        </TableCell>
                      </TableRow>
                    ) : (
                      errorPatternData.map((row, idx) => {
                        const { display: errorTypeDisplay, status: errorTypeStatus } = getErrorTypeDisplay(row);
                        const rowId = (row as any).id;
                        const hasEditId = rowId != null && rowId !== '' && !Number.isNaN(Number(rowId));
                        return (
                        <TableRow
                          key={`errorpattern-${rowId ?? idx}-${row.clientName || idx}-${row.errorCode || idx}`}
                          onClick={() => hasEditId && navigate(`/error-pattern-edit/${rowId}`)}
                          sx={{
                            cursor: hasEditId ? 'pointer' : 'default',
                            bgcolor: idx % 2 === 0 ? SAP_COLORS.sapWhite : SAP_COLORS.sapGray1,
                            '&:hover': { 
                              bgcolor: SAP_COLORS.hover,
                            },
                            transition: 'background-color 0.2s ease',
                          }}
                        >
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray6, 
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium 
                          }}>
                            {page * ROWS_PER_PAGE + idx + 1}
                          </TableCell>
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray7,
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                          }}>
                            {row.clientName || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ color: SAP_COLORS.sapGray6 }}>
                            {row.pattern || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ color: SAP_COLORS.sapGray6 }}>
                            {row.errorCode || 'N/A'}
                          </TableCell>
                          <TableCell>
                            <SAPStatusChip status={errorTypeStatus}>
                              {errorTypeDisplay}
                            </SAPStatusChip>
                          </TableCell>
                        </TableRow>
                      ); })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination */}
              {errorPatternData.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {/* Pagination Info */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {(page * ROWS_PER_PAGE) + 1}-{Math.min((page * ROWS_PER_PAGE) + errorPatternData.length, rowCount)} of {rowCount} error patterns
                    </Typography>
                  </Box>
                  
                  {/* Pagination Controls */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Rows per page</InputLabel>
                      <Select
                        value={ROWS_PER_PAGE}
                        onChange={(event: SelectChangeEvent<number>) => {
                          // Note: ROWS_PER_PAGE is constant, so this is just for UI consistency
                        }}
                        label="Rows per page"
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <Pagination
                      count={Math.ceil(rowCount / ROWS_PER_PAGE)}
                      page={page + 1}
                      onChange={(event: React.ChangeEvent<unknown>, newPage: number) => {
                        handleChangePage(event, newPage - 1);
                      }}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </SAPDataCard>

      {/* Create Error Pattern dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={handleCreateClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${SAP_COLORS.sapGray2}`,
            boxShadow: SAP_SHADOWS.card,
          },
        }}
      >
        <DialogTitle sx={{ color: SAP_COLORS.sapGray7, fontWeight: SAP_TYPOGRAPHY.fontWeight.bold, borderBottom: `1px solid ${SAP_COLORS.sapGray2}`, pb: 2 }}>
          Create Error Pattern
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Client: required for admin; for non-admin pre-filled from context */}
            <Autocomplete
              options={createClientOptions}
              value={createClient}
              onChange={(_, v) => setCreateClient(v)}
              getOptionLabel={(option) => option?.label ?? ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client"
                  size="small"
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': { bgcolor: SAP_COLORS.sapWhite },
                    '& .MuiInputLabel-root': { color: SAP_COLORS.sapGray5 },
                  }}
                />
              )}
              disabled={createSaving || (!isAdmin && createClientOptions.length <= 1)}
              isOptionEqualToValue={(a, b) => a?.value === b?.value}
            />
            <Autocomplete
              options={errorTypeOptions}
              value={createErrorType}
              onChange={(_, v) => setCreateErrorType(v)}
              getOptionLabel={(option) => option?.label ?? ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Error Type"
                  size="small"
                  required
                  placeholder="e.g. TECHNICAL_ERROR, BUSINESS_ERROR"
                  sx={{
                    '& .MuiOutlinedInput-root': { bgcolor: SAP_COLORS.sapWhite },
                    '& .MuiInputLabel-root': { color: SAP_COLORS.sapGray5 },
                  }}
                />
              )}
              disabled={createSaving}
              isOptionEqualToValue={(a, b) => a?.value === b?.value}
            />
            <TextField
              label="Pattern"
              value={createPattern}
              onChange={(e) => setCreatePattern(e.target.value)}
              size="small"
              required
              fullWidth
              placeholder="Enter pattern"
              disabled={createSaving}
              sx={{
                '& .MuiOutlinedInput-root': { bgcolor: SAP_COLORS.sapWhite },
                '& .MuiInputLabel-root': { color: SAP_COLORS.sapGray5 },
              }}
            />
            <TextField
              label="Error Code"
              value={createErrorCode}
              onChange={(e) => setCreateErrorCode(e.target.value)}
              size="small"
              required
              fullWidth
              placeholder="Enter error code"
              disabled={createSaving}
              sx={{
                '& .MuiOutlinedInput-root': { bgcolor: SAP_COLORS.sapWhite },
                '& .MuiInputLabel-root': { color: SAP_COLORS.sapGray5 },
              }}
            />
            {createError && (
              <Alert severity="error" onClose={() => setCreateError(null)}>
                {createError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${SAP_COLORS.sapGray2}`, gap: 1 }}>
          <Button
            onClick={handleCreateClose}
            disabled={createSaving}
            sx={{ color: SAP_COLORS.sapGray6, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSave}
            disabled={createSaving || !createPattern.trim() || !createErrorCode.trim() || !createErrorType?.value || !createClient?.value}
            sx={{
              backgroundColor: SAP_COLORS.sapGreen,
              '&:hover': { backgroundColor: '#267338' },
              textTransform: 'none',
            }}
          >
            {createSaving ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}